import { getOrderedCategories, type CategoryDef } from '@/entities/clients-categories';
import { $clientsCategoryOrder, $reorderClientsCategory } from '@/entities/clients-categories-order.entity';
import type { ClientsListType } from '@/entities/clients-filter.entity';
import { useHorizontalWheelScroll } from '@/hooks/use-horizontal-wheel-scroll';
import { cn } from '@/lib/utils';
import {
	DndContext,
	DragOverlay,
	type DragEndEvent,
	type DragStartEvent,
	MouseSensor,
	TouchSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useUnit } from 'effector-react';
import { forwardRef, useCallback, useEffect, useMemo, useState, type ButtonHTMLAttributes } from 'react';

const DESKTOP_DRAG_ACTIVATION = { delay: 450, tolerance: 8 } as const;
const TOUCH_DRAG_ACTIVATION = { delay: 450, tolerance: 14 } as const;

interface CategoryTabsProps {
	value: ClientsListType;
	onChange: (value: ClientsListType) => void;
}

function hapticImpact() {
	window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('medium');
}

interface CategoryTabButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	cat: CategoryDef;
	active: boolean;
}

const CategoryTabButton = forwardRef<HTMLButtonElement, CategoryTabButtonProps>(function CategoryTabButton(
	{ cat, active, className, type = 'button', ...props },
	ref,
) {
	const Icon = cat.icon;

	return (
		<button
			ref={ref}
			type={type}
			className={cn(
				'shrink-0 h-8 px-3 rounded-md text-sm inline-flex items-center gap-1.5 border transition-colors select-none',
				'[-webkit-touch-callout:none]',
				active ? 'bg-secondary border-border text-foreground font-medium' : 'bg-transparent border-transparent text-muted-foreground',
				className,
			)}
			{...props}
		>
			<Icon size={15} className={cn(active && 'text-chart-2')} />
			{cat.label}
			{!cat.ready && <span className='text-[10px] opacity-50'>soon</span>}
		</button>
	);
});

interface SortableCategoryTabProps {
	cat: CategoryDef;
	active: boolean;
	isDragSession: boolean;
	onChange: (value: ClientsListType) => void;
}

function SortableCategoryTab({ cat, active, isDragSession, onChange }: SortableCategoryTabProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: cat.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		touchAction: isDragSession ? 'none' : 'manipulation',
	};

	return (
		<CategoryTabButton
			ref={setNodeRef}
			style={style}
			cat={cat}
			active={active}
			{...attributes}
			{...listeners}
			onClick={() => onChange(cat.id)}
			className={cn(isDragging ? 'opacity-40 cursor-grabbing' : 'cursor-pointer')}
		/>
	);
}

export default function CategoryTabs({ value, onChange }: CategoryTabsProps) {
	const scrollRef = useHorizontalWheelScroll<HTMLDivElement>();
	const [order, reorderCategory] = useUnit([$clientsCategoryOrder, $reorderClientsCategory]);
	const [activeDragId, setActiveDragId] = useState<ClientsListType | null>(null);

	const categories = useMemo(() => getOrderedCategories(order), [order]);
	const categoryIds = useMemo(() => categories.map(c => c.id), [categories]);
	const activeDragCategory = useMemo(
		() => (activeDragId ? categories.find(cat => cat.id === activeDragId) : undefined),
		[activeDragId, categories],
	);

	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: DESKTOP_DRAG_ACTIVATION }),
		useSensor(TouchSensor, { activationConstraint: TOUCH_DRAG_ACTIVATION }),
	);

	useEffect(() => {
		if (!activeDragId) return;

		const prev = document.body.style.touchAction;
		document.body.style.touchAction = 'none';
		return () => {
			document.body.style.touchAction = prev;
		};
	}, [activeDragId]);

	const handleDragStart = useCallback((event: DragStartEvent) => {
		setActiveDragId(event.active.id as ClientsListType);
		hapticImpact();
	}, []);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			setActiveDragId(null);

			const { active, over } = event;
			if (!over || active.id === over.id) return;

			reorderCategory({
				activeId: active.id as ClientsListType,
				overId: over.id as ClientsListType,
			});
		},
		[reorderCategory],
	);

	const handleDragCancel = useCallback(() => {
		setActiveDragId(null);
	}, []);

	return (
		<div
			ref={scrollRef}
			className={cn(
				'overflow-x-auto scrollbar-hide -mx-0.5 px-0.5',
				activeDragId ? 'overflow-x-hidden touch-none overscroll-none' : 'touch-pan-x',
			)}
		>
			<DndContext
				sensors={sensors}
				collisionDetection={closestCenter}
				onDragStart={handleDragStart}
				onDragEnd={handleDragEnd}
				onDragCancel={handleDragCancel}
			>
				<SortableContext items={categoryIds} strategy={horizontalListSortingStrategy}>
					<div className='flex gap-1.5 w-max min-w-full'>
						{categories.map(cat => (
							<SortableCategoryTab
								key={cat.id}
								cat={cat}
								active={value === cat.id}
								isDragSession={activeDragId !== null}
								onChange={onChange}
							/>
						))}
					</div>
				</SortableContext>
				<DragOverlay dropAnimation={null} adjustScale={false}>
					{activeDragCategory ? (
						<CategoryTabButton
							cat={activeDragCategory}
							active={value === activeDragCategory.id}
							className='shadow-md scale-[1.02] z-50 cursor-grabbing bg-secondary'
							tabIndex={-1}
							aria-hidden
						/>
					) : null}
				</DragOverlay>
			</DndContext>
		</div>
	);
}
